import React, { useState, useEffect, useRef } from 'react'
import { Modal } from 'react-bootstrap'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { getAvailableDriverAvatars } from '../../action/DriverActions'
import CustomButton from '../../component/CustomButton'
import '../../component/smart/modals/Modal.sass'
import './ModalDriverAvatarGallery.sass'

export function ModalDriverAvatarGallery(props) {
	const { driverId, selectedPhotoIds, assignedTripPhotoIds, onSelect, onCancel, toggleLoader } = props;
	const { t } = useTranslation('translation', { keyPrefix: 'driversPage.modalAddAndEditDriver' });
	const [availablePhotos, setAvailablePhotos] = useState([]);
	const [pendingPhotoIds, setPendingPhotoIds] = useState([]);
	const loadNextPage = useRef(null);

	useEffect(() => {
		let active = true;
		let loading = false;
		let hasMore = true;
		let pageNumber = 1;
		let receivedPhotos = 0;
		setAvailablePhotos([]);
		setPendingPhotoIds([]);
		const loadPage = () => {
			if (!active || loading || !hasMore) return;
			loading = true;
			toggleLoader(true);
			return getAvailableDriverAvatars({ driver_id: driverId, page_number: pageNumber, page_size: 50 })
				.then(res => {
					if (!active) return;
					const response = res.data.response;
					const page = response.trips_photos || [];
					const photos = _.uniqBy(page, 'id')
						.filter(photo => !assignedTripPhotoIds.includes(photo.id))
						.map(photo => ({ ...photo, driver_trip_photo_id: photo.id, id: `trip_${photo.id}` }));
					receivedPhotos += page.length;
					hasMore = page.length > 0 && receivedPhotos < response.total_number;
					pageNumber += 1;
					setAvailablePhotos(previous => _.uniqBy([...previous, ...photos], 'id'));
					setPendingPhotoIds(ids => _.uniq([...ids, ...photos.filter(photo => selectedPhotoIds.includes(photo.id)).map(photo => photo.id)]));
				})
				.catch(error => {
					if (!active) return;
					console.log('!getAvailableDriverAvatars error: ', error.response, error);
				})
				.finally(() => {
					loading = false;
					if (active) toggleLoader(false);
				});
		};
		loadNextPage.current = loadPage;
		loadPage();
		return () => {
			active = false;
			loadNextPage.current = null;
			toggleLoader(false);
		};
	}, []);

	const handlePhotosScroll = (event) => {
		const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
		if (scrollHeight - scrollTop - clientHeight <= 75) {
			loadNextPage.current?.();
		}
	};

	const handlePhotosWheel = (event) => {
		const { scrollHeight, clientHeight } = event.currentTarget;
		if (event.deltaY > 0 && scrollHeight <= clientHeight) {
			loadNextPage.current?.();
		}
	};

	const togglePhoto = (photoId) => {
		setPendingPhotoIds(ids => ids.includes(photoId) ? ids.filter(id => id !== photoId) : [...ids, photoId]);
	};

	const selectAvatars = () => {
		if (availablePhotos.length && pendingPhotoIds.length) {
			onSelect(availablePhotos.filter(photo => pendingPhotoIds.includes(photo.id)), availablePhotos.map(photo => photo.id));
		}
	};

	return (
		<Modal
			show
			onHide={onCancel}
			className="modal-lg-size driver-modal driver-avatars-modal"
		>
			<Modal.Header closeButton>
				<Modal.Title>{t('titleAddAvatars')}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="photos-list" onScroll={handlePhotosScroll} onWheel={handlePhotosWheel} onTouchMove={handlePhotosScroll}>
					{!availablePhotos.length && <div className="no-avatars">{t('noAvatarsAvailable')}</div>}
					{availablePhotos.map(photo =>
						<button
							type="button"
							key={photo.id}
							className="available-avatar"
							aria-pressed={pendingPhotoIds.includes(photo.id)}
							onClick={() => togglePhoto(photo.id)}
						>
							<img className={classnames({ active: pendingPhotoIds.includes(photo.id) })} src={photo.url || photo.snapshot_url || "/images/unknown_driver.svg"} alt="Avatar" />
						</button>
					)}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<span className="selected-avatars-count" aria-live="polite">{t('selectedAvatarsCount', { count: availablePhotos.length ? pendingPhotoIds.length : 0 })}</span>
				<CustomButton variant="primary-outline" size="sm" onClick={onCancel}>{t('btnCancel')}</CustomButton>
				<CustomButton variant="primary" size="sm" disabled={!availablePhotos.length || !pendingPhotoIds.length} onClick={selectAvatars}>{t('btnSelectAvatars')}</CustomButton>
			</Modal.Footer>
		</Modal>
	);
}

export default ModalDriverAvatarGallery;
